import { Box, Typography } from '@mui/material';
import { SxProps, useTheme } from '@mui/material/styles';
import EditorBar from '../EditorBar/EditorBar';
import { defaultToolbarButtons } from '../EditorBar/const';
import { EditorBarStateProps, useEditorBarState } from '../EditorBar/useEditorBarState';
import RichTextInput, {
  RichTextInputTypes,
  richTextLabelTypographySx,
} from '../RichTextInput/RichTextInput';
import {
  getEditorWithBarToolbarSx,
  getEditorWithBarInputSx,
  getEditorWithBarWrapperSx,
} from './EditorWithBarSx';
import { TToolbarProps } from '../EditorBar/types';
import WordCount from '../RichTextInput/WordCount/WordCount';
import { EditorSetupConfig } from 'screens/editorial/asset/utility/getEditorConfigs';
import { EditorBubbleMenuProvider } from '../EditorBubbleMenu/context';
import EditorBubbleMenu from '../EditorBubbleMenu/EditorBubbleMenu';

// EditorWithBarProps extends some RichTextInput properties
// as certain props of the setup are destructured and used per piece
// in EWB and the some are selectively passed to RichTextInput

export type EditorWithBarProps = RichTextInputTypes & {
  labelSx?: SxProps;
  tooltipPlacement?: TToolbarProps['tooltipPlacement'];
  toolbarButtons?: EditorSetupConfig['toolbarButtons'];
  disableDropCursor?: EditorSetupConfig['disableDropCursor'];
};

const EditorWithBar = ({
  labelSx,
  tooltipPlacement,
  toolbarButtons = defaultToolbarButtons,
  disableDropCursor,
  // selected rti inherited, also used in ewb
  editor,
  id,
  maxLength,
  showCount,
  label,
  sx,
  disabled,
  // rest props to fwd to rti
  ...richTextInputProps
}: EditorWithBarProps) => {
  const theme = useTheme();

  const { isToolbarEnabled } = useEditorBarState(
    [{ editor: editor, toolbar: true }].filter(
      ({ editor }) => editor !== undefined,
    ) as EditorBarStateProps[],
  );

  // Setup IDs
  const baseEwbId = 'editorwithbar';
  const containerEwbId = `${baseEwbId}-container`;
  const labelEwbId = `${baseEwbId}-label`;
  const wrapperEwbId = `${baseEwbId}-wrapper`;
  // allow editorConfig to override RTI id if specified
  const editorId = id || baseEwbId;
  // toolbar id derive from editorId
  const toolbarEwbId = `${editorId}-toolbar`;

  if (!editor) {
    return null;
  }

  return (
    <EditorBubbleMenuProvider editor={editor}>
      <Box
        id={containerEwbId}
        className={containerEwbId}
        data-bubble-boundary
        sx={{
          color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        {label && (
          <Typography
            id={labelEwbId}
            className={labelEwbId}
            sx={{
              ...richTextLabelTypographySx,
              ...(typeof labelSx === 'function' ? labelSx(theme) : labelSx),
            }}
          >
            {label}
          </Typography>
        )}
        <Box
          id={wrapperEwbId}
          className={wrapperEwbId}
          sx={{
            ...getEditorWithBarWrapperSx(theme),
            ...(typeof sx === 'function' ? sx(theme) : sx),
          }}
        >
          <EditorBar
            id={toolbarEwbId}
            data-testid={toolbarEwbId}
            editor={editor}
            sx={getEditorWithBarToolbarSx}
            tooltipPlacement={tooltipPlacement || 'top'}
            disabled={disabled && !isToolbarEnabled}
            buttons={toolbarButtons}
          />

          <RichTextInput
            editor={editor}
            id={editorId}
            sx={(theme) => getEditorWithBarInputSx(theme)}
            disabled={disabled}
            disableDropCursor={disableDropCursor}
            {...richTextInputProps}
          />
        </Box>

        <EditorBubbleMenu editor={editor} />

        {showCount && (
          <WordCount
            editor={editor}
            maxLength={maxLength}
            display={showCount}
          />
        )}
      </Box>
    </EditorBubbleMenuProvider>
  );
};

export default EditorWithBar;
